import { inject, Service } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import type { Observable, ObservableInput } from 'rxjs';
import { BehaviorSubject, catchError, filter, map, of, switchMap, tap } from 'rxjs';
import { retry } from 'rxjs/operators';
import type { FlipperConfiguration } from '../infrastructure';
import { FlipperException } from '../infrastructure';
import { BROWSER_DATA, FLIPPER_CONFIGURATION } from './injection-tokens';
import { Storage } from './storage';
import type { CachingConfiguration } from '../infrastructure/configuration';
import type { FeatureFlag, FeatureResponse } from '../models/flipper-feature';
import type { UserContext } from '../models/user-context.type';

/**
 * Service for evaluating feature flags
 * @description This service allows you to evaluate feature flags based on user context.
 * It also allows you to add additional context to the user context.
 * If caching is enabled, the result of the evaluation will be cached and will only re-evaluate if the user context changes.
 */
@Service({ autoProvided: false })
export class Flipper {
  private config = inject<FlipperConfiguration>(FLIPPER_CONFIGURATION);
  private browserData = inject<UserContext>(BROWSER_DATA);
  private readonly storageService = inject(Storage);
  private readonly http = inject(HttpClient);

  private readonly featureUrl: string;
  private userContext$ = new BehaviorSubject<UserContext>({});
  private features$: BehaviorSubject<FeatureResponse> = new BehaviorSubject<FeatureResponse>({ features: [] });

  constructor() {
    const config = this.config;

    if (!config.appId) {
      throw new FlipperException('appId is required');
    }

    this.featureUrl = (config.apiUrl ? config.apiUrl + '/' : '/') + (config.basePath ?? 'api/v1/feature-management');

    if (this.config.caching) {
      const cacheConfig = this.config.caching;
      // load context from storage
      const context = this.storageService.get<UserContext>(cacheConfig.userContextCacheKey);

      if (context) {
        this.userContext$.next(context);
      }

      // side effect: on change, save to storage
      this.userContext$.pipe().subscribe(ctx => {
        this.storageService.set(cacheConfig.userContextCacheKey, ctx);
      });
    }
  }

  /**
   * Evaluate the feature flags based on the user context
   * @param {UserContext} userContext - key-value pairs of user context. e.g. { companyname: 'My Company, userId: '123', industryCode: '24.50' }
   * @returns {Observable<FeatureResponse | null>} Observable of feature response or null
   */
  evaluate$(userContext?: UserContext): Observable<FeatureResponse | null> {
    // flow:
    // 1. check if userContext argument is considered different compared to the last one
    // 2. if different, clear cache
    // 3. if not, get from cache
    // 4. if not found in cache, get from server

    userContext ??= this.userContext$.value ?? { features: [] };

    // append browser data if available
    if (this.browserData) {
      userContext = { ...userContext, ...this.browserData };
    }

    // check if userContext is different
    const lastContext = this.userContext$.value;
    if (!this.isNewContextIdentical(userContext, lastContext)) {
      if (this.config.caching) {
        this.clearCache(this.config.caching);
      }
      this.userContext$.next(userContext);
    }

    // attempt to get from cache
    const cache = this.config.caching ? this.getCachedData(this.config.caching) : undefined;
    if (cache) {
      this.features$.next(cache);
      return this.features$.asObservable();
    }

    // make request
    const featureRequest = {
      applicationId: this.config.appId,
      userContext: userContext
    };

    return this.createEvaluationRequest$(featureRequest.applicationId, featureRequest.userContext, this.config);
  }

  /**
   * add additional context to the user context. This will trigger a re-evaluation of the feature flags
   * @param {UserContext} context - The context to add
   * @returns {Observable<FeatureResponse | null>} Observable of feature response or null
   */
  addContext$(context: UserContext): Observable<FeatureResponse | null> {
    return of(context).pipe(
      switchMap(ctx => {
        const currentContext = this.userContext$.value;
        const newContext = { ...currentContext, ...ctx };
        return this.evaluate$(newContext);
      })
    );
  }

  /**
   * Get a feature flag by name
   * @description Requires a successful evaluation before yielding any results.
   * Name comparison is case-insensitive.
   * @param {string} featureName - The name of the feature flag
   * @returns {Observable<FeatureFlag | null>} Observable of feature flag or null
   */
  getFeatureFlag$(featureName: string): Observable<FeatureFlag | null> {
    return this.features$.pipe(
      filter(Boolean),
      map(data => data.features),
      map(features => features.find(f => f.featureName.toLowerCase() === featureName.toLowerCase()) ?? null)
    );
  }

  /**
   * Clear the cache
   * @description This will clear the cache and evaluate() must be called again to get the latest feature flags
   * @param {CachingConfiguration} cacheConfig - The caching configuration
   * @returns {void}
   */
  clearCache(cacheConfig: CachingConfiguration): void {
    this.features$.next({ features: [] });
    this.storageService.remove(cacheConfig.featureFlagCacheKey);
  }

  /**
   * Get cached data from provided storage service
   * @private
   * @param {CachingConfiguration} cacheConfig - The caching configuration
   * @returns {FeatureResponse | null} The cached feature response or null
   */
  private getCachedData(cacheConfig: CachingConfiguration): FeatureResponse | null {
    return this.storageService.get<FeatureResponse>(cacheConfig.featureFlagCacheKey);
  }

  /**
   * Cache data using the provided storage service
   * @private
   * @param {FeatureResponse} data - The data to cache
   * @param {CachingConfiguration} cacheConfig - The caching configuration
   * @returns {void}
   */
  private cacheData(data: FeatureResponse, cacheConfig: CachingConfiguration): void {
    if (!data) {
      return;
    }

    this.features$.next(data);
    this.storageService.set(cacheConfig.featureFlagCacheKey, data, cacheConfig.ttl ?? undefined);
  }

  /**
   * Make the request to the feature service. This will also cache the result.
   * @private
   * @param {string} applicationId - the application id
   * @param {UserContext} userContext - The user context in form of key-value pairs
   * @param {FlipperConfiguration} config - The flipper configuration
   * @returns {Observable<FeatureResponse>} Observable of feature response
   */
  private createEvaluationRequest$(
    applicationId: string,
    userContext: UserContext,
    config: FlipperConfiguration
  ): Observable<FeatureResponse> {
    let request$ = this.http.post<FeatureResponse>(`${this.featureUrl}/evaluate-context`, {
      applicationId,
      userContext
    });

    // if httpSettings is not set, return the request as is. Do not retry or catch errors
    if (!config.httpSettings) {
      return request$;
    }

    request$ = request$.pipe(
      retry(config.httpSettings.retryConfig),
      catchError(this.handleError$<FeatureResponse>),
      tap({
        next: res => {
          // do not cache is null
          if (!config.caching) {
            return;
          }
          this.cacheData(res, config.caching);
        }
      })
    );

    return request$;
  }

  /**
   * Compare two contexts.
   * @description
   * * If the new context has more keys than the old context, it is considered different.
   * * If the new context has fewer keys than the old context, but the remaining key/values are identical, the contexts are considered identical.
   * @private
   * @param {UserContext} newContext - The new context to compare
   * @param {UserContext} oldContext - The old context to compare against
   * @returns {boolean} True if contexts are identical, false otherwise
   */
  private isNewContextIdentical(newContext: UserContext, oldContext: UserContext): boolean {
    if (Object.keys(newContext).length > Object.keys(oldContext).length) {
      return false;
    }

    for (const key in newContext) {
      if (newContext[key] !== oldContext[key]) {
        return false;
      }
    }

    return true;
  }

  /**
   * Handle HTTP errors
   * @private
   * @param {Error} err - The error to handle
   * @returns {ObservableInput<T>} Observable input of type T
   */
  private handleError$<T>(err: Error): ObservableInput<T> {
    // log error
    if (err instanceof HttpErrorResponse) {
      if (err.status === 0) {
        const ex = new FlipperException(`[HTTP_ERROR]: Unknown network error: ${err.statusText}`);
        ex.innerException = err;
        throw ex;
      } else {
        const ex = new FlipperException(`[HTTP_ERROR] ${err.status} - ${err.statusText}`);
        ex.innerException = err;
        throw ex;
      }
    } else {
      const ex = new FlipperException(`[ERROR] Unknown Error`);
      ex.innerException = err;
      throw ex;
    }
  }
}
